import { appTokenAuthContext } from 'microsites/partner-linking/app-token';
import uuid from 'uuid';

import { useCreateOtpMutation, useResendVerificationEmailMutation } from 'generated/rbi-graphql';
import { useAuthContext } from 'state/auth';
import { storeOtpCredentials } from 'state/auth/hooks/use-account-authentication';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLogger } from 'state/logger';
import { platform, welcomeEmailDomain } from 'utils/environment';
import { parseJwt } from 'utils/jwt';
import { routes } from 'utils/routing';

interface IUseUserVerificationOptions {
  /**
   * Send as this ID token's identity rather than the current web session.
   *
   * `resendVerificationEmail` carries no address — the gateway mails whoever the
   * request authenticates as. An app-handoff guest has no session of their own,
   * and the browser may hold a different guest's, so their send must carry the
   * token. Omit for the ordinary web flow.
   */
  idToken?: string | null;
  /**
   * Allow the `EXP_UNIFIED_AUTH_ENTRY_WEB` hand-off, which signs the guest out
   * and leaves for `confirmOtp`. Pass `false` from a flow that navigating away
   * would abandon.
   */
  allowOtpHandoff?: boolean;
}

const useUserVerification = ({
  idToken,
  allowOtpHandoff = true,
}: IUseUserVerificationOptions = {}) => {
  const logger = useLogger();
  const { user, signOut } = useAuthContext();
  const { navigate } = useLocationContext();
  const isUnifiedAuthEntry = useFlag(LaunchDarklyFlag.EXP_UNIFIED_AUTH_ENTRY_WEB);
  const [
    resendVerificationEmail,
    { loading: resendLoading },
  ] = useResendVerificationEmailMutation();
  const [createOtpMutation, { loading: createOtpLoading }] = useCreateOtpMutation();

  // Guards the send and feeds the OTP input; the gateway resolves the real
  // recipient. A token identity has no profile, so read its claims.
  const email = idToken ? parseJwt(idToken)?.email : user?.details.email;

  /** Resolves to whether the guest can now expect an email. */
  const sendVerificationEmail = async (): Promise<boolean> => {
    if (!email) {
      logger.error({ message: 'Failed to send verification: missing user email' });
      return false;
    }

    try {
      // The hand-off signs the web session out and leaves for `confirmOtp`. That
      // session is a bystander to a token identity, and callers mid-flow opt out
      // entirely, so both cases send the plain verification email instead.
      if (isUnifiedAuthEntry && allowOtpHandoff && !idToken) {
        const sessionId = uuid();
        await createOtpMutation({
          variables: {
            input: {
              email,
              platform: platform(),
              sessionId,
            },
          },
        });
        await signOut();
        storeOtpCredentials({ email, sessionId });
        navigate(routes.confirmOtp);
        return true;
      }

      await resendVerificationEmail({
        variables: {
          input: {
            stage: welcomeEmailDomain(),
            platform: platform(),
          },
        },
        // Displaces the session token `withAuthToken` would otherwise attach.
        ...(idToken ? { context: appTokenAuthContext(idToken) } : {}),
      });
      return true;
    } catch (error) {
      logger.error({ error, message: 'Failed to resend verification link' });
      return false;
    }
  };

  return {
    loading: resendLoading || createOtpLoading,
    sendVerificationEmail,
  };
};

export default useUserVerification;
